import { useState, useEffect, useCallback, useRef } from 'react'
import { Lock, User, AlertCircle, Loader2, X, RefreshCw } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'

/**
 * 모바일 인라인 재로그인 모달
 * - 401 에러 발생 시 현재 페이지 위에 오버레이로 표시
 * - 재로그인 성공 시 모달만 닫고 페이지 유지 (새로고침 없음)
 * - window 이벤트 'cams:auth-expired' 수신
 */
export default function MobileReLoginModal() {
  const [visible, setVisible] = useState(false)
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const { login, user, isAuthenticated } = useAuthStore()
  // 재로그인 성공 후 일정 시간 이벤트 무시
  const suppressUntilRef = useRef(0)
  const dialogRef = useRef(null)
  const previousActiveElementRef = useRef(null)

  // 이전 사용자 정보 pre-fill
  useEffect(() => {
    if (user?.username) {
      setUsername(user.username)
    }
  }, [user])

  // 401 이벤트 수신
  const handleAuthExpired = useCallback((e) => {
    // 유예 구간이면 무시
    if (Date.now() < suppressUntilRef.current) return
    // 이미 표시 중이면 무시
    if (visible) return
    previousActiveElementRef.current = document.activeElement
    setVisible(true)
    setError('')
    setPassword('')
  }, [visible])

  useEffect(() => {
    window.addEventListener('cams:auth-expired', handleAuthExpired)
    return () => window.removeEventListener('cams:auth-expired', handleAuthExpired)
  }, [handleAuthExpired])

  const handleDismiss = useCallback(() => {
    setVisible(false)
    setPassword('')
    setError('')
  }, [])

  // 모달 표시 중 배경 스크롤을 고정하고 키보드 초점을 모달 안에 유지한다.
  useEffect(() => {
    if (!visible) return undefined

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleDismiss()
        return
      }

      if (event.key !== 'Tab' || !dialogRef.current) return

      const focusableElements = Array.from(dialogRef.current.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
      )).filter((element) => element.getClientRects().length > 0)

      if (focusableElements.length === 0) {
        event.preventDefault()
        return
      }

      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]
      const activeElement = document.activeElement

      const focusIsOutsideDialog = !dialogRef.current.contains(activeElement)

      if (event.shiftKey && (activeElement === firstElement || focusIsOutsideDialog)) {
        event.preventDefault()
        lastElement.focus()
      } else if (!event.shiftKey && (activeElement === lastElement || focusIsOutsideDialog)) {
        event.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow

      const previousActiveElement = previousActiveElementRef.current
      if (previousActiveElement?.isConnected && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus({ preventScroll: true })
      }
      previousActiveElementRef.current = null
    }
  }, [handleDismiss, visible])

  const handleLogin = async (e) => {
    e.preventDefault()
    if (!username.trim() || !password.trim()) {
      setError('아이디와 비밀번호를 입력해주세요.')
      return
    }

    setLoading(true)
    setError('')

    try {
      const result = await login(username.trim(), password.trim())
      if (result.success) {
        // 재로그인 성공 후 5초간 세션만료 이벤트 무시
        suppressUntilRef.current = Date.now() + 5000
        setVisible(false)
        setPassword('')
        setError('')
        // 페이지 이동 없이 현재 상태 유지 — 필요한 경우 데이터 재로드
        window.dispatchEvent(new CustomEvent('cams:auth-restored'))
      } else {
        setError(result.error || '로그인에 실패했습니다.')
      }
    } catch (err) {
      setError('로그인 중 오류가 발생했습니다.')
    } finally {
      setLoading(false)
    }
  }

  if (!visible) return null

  return (
    <div className="mobile-relogin-overlay fixed inset-0 z-[9999] flex items-end sm:items-center justify-center">
      {/* 오버레이 */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />
      
      {/* 모달 */}
      <div
        ref={dialogRef}
        className="mobile-relogin-dialog relative w-full bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl animate-slide-up"
        role="dialog"
        aria-modal="true"
        aria-labelledby="mobile-relogin-title"
        aria-describedby="mobile-relogin-description"
      >
        <div className="mobile-relogin-content">
        {/* 닫기 버튼 */}
        <button
          type="button"
          onClick={handleDismiss}
          className="mobile-relogin-close absolute top-3 right-3 min-h-[44px] min-w-[44px] inline-flex items-center justify-center p-1 text-gray-400 hover:text-gray-600 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          aria-label="재로그인 창 닫기"
        >
          <X size={20} />
        </button>

        {/* 헤더 */}
        <div className="mobile-relogin-header text-center">
          <div className="w-14 h-14 mx-auto mb-3 bg-orange-100 rounded-full flex items-center justify-center">
            <Lock size={28} className="text-orange-500" />
          </div>
          <h3 id="mobile-relogin-title" className="text-lg font-bold text-gray-900">세션이 만료되었습니다</h3>
          <p id="mobile-relogin-description" className="text-sm text-gray-500 mt-1">
            다시 로그인하면 현재 작업을 계속할 수 있습니다
          </p>
        </div>

        {/* 에러 메시지 */}
        {error && (
          <div className="mobile-relogin-error flex items-start gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" role="alert">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* 로그인 폼 */}
        <form onSubmit={handleLogin} className="mobile-relogin-form space-y-4">
          <div>
            <label htmlFor="mobile-relogin-username" className="block text-sm font-medium text-gray-700 mb-1">아이디</label>
            <div className="relative">
              <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                id="mobile-relogin-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full min-h-[48px] pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="아이디 입력"
                autoComplete="username"
                autoFocus={!username}
              />
            </div>
          </div>

          <div>
            <label htmlFor="mobile-relogin-password" className="block text-sm font-medium text-gray-700 mb-1">비밀번호</label>
            <div className="relative">
              <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                id="mobile-relogin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full min-h-[48px] pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-base"
                placeholder="비밀번호 입력"
                autoComplete="current-password"
                autoFocus={!!username}
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full min-h-[48px] py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" />
                로그인 중...
              </>
            ) : (
              <>
                <RefreshCw size={18} />
                다시 로그인
              </>
            )}
          </button>
        </form>

        {/* 하단 안내 */}
        <p className="mobile-relogin-footnote text-xs text-gray-400 text-center">
          로그인 후 현재 페이지가 유지됩니다
        </p>
        </div>
      </div>

      <style>{`
        .mobile-relogin-overlay {
          height: 100vh;
          height: 100dvh;
          padding-top: max(12px, env(safe-area-inset-top));
          padding-right: max(12px, env(safe-area-inset-right));
          padding-left: max(12px, env(safe-area-inset-left));
        }
        .mobile-relogin-dialog {
          max-width: 32rem;
          max-height: calc(100vh - max(12px, env(safe-area-inset-top)));
          max-height: calc(100dvh - max(12px, env(safe-area-inset-top)));
          overflow-y: auto;
          overscroll-behavior: contain;
          padding-bottom: max(24px, env(safe-area-inset-bottom));
        }
        .mobile-relogin-content {
          padding: 24px;
          padding-bottom: 0;
        }
        .mobile-relogin-header {
          margin-bottom: 24px;
        }
        .mobile-relogin-error {
          margin-bottom: 16px;
        }
        .mobile-relogin-footnote {
          margin-top: 16px;
        }
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        @media (min-width: 640px) {
          .mobile-relogin-overlay {
            padding-bottom: max(12px, env(safe-area-inset-bottom));
          }
          .mobile-relogin-dialog {
            width: min(88vw, 46rem);
            max-width: 46rem;
            max-height: calc(100vh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
            max-height: calc(100dvh - 24px - env(safe-area-inset-top) - env(safe-area-inset-bottom));
            padding-bottom: 0;
          }
          .mobile-relogin-content {
            padding: 32px;
          }
        }
        @media (min-width: 768px) and (orientation: landscape) {
          .mobile-relogin-dialog {
            width: min(86vw, 56rem);
            max-width: 56rem;
          }
          .mobile-relogin-content {
            display: grid;
            grid-template-columns: minmax(13rem, 0.8fr) minmax(20rem, 1.2fr);
            grid-template-areas:
              "header error"
              "header form"
              "header footnote";
            column-gap: 40px;
            align-items: center;
          }
          .mobile-relogin-header {
            grid-area: header;
            margin-bottom: 0;
          }
          .mobile-relogin-error {
            grid-area: error;
            margin: 0 0 12px;
          }
          .mobile-relogin-form {
            grid-area: form;
          }
          .mobile-relogin-footnote {
            grid-area: footnote;
            margin-top: 12px;
          }
        }
        @media (orientation: landscape) and (max-height: 560px) {
          .mobile-relogin-content {
            padding-top: 20px;
            padding-bottom: 20px;
          }
          .mobile-relogin-header .w-14 {
            width: 44px;
            height: 44px;
            margin-bottom: 8px;
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .animate-slide-up {
            animation: none;
          }
        }
      `}</style>
    </div>
  )
}
